import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { TryOnCreditsProvider } from "@/context/TryOnCreditsContext";
import { SideCart } from "@/components/SideCart";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import TryOn from "@/pages/TryOn";
import TryOnPricing from "@/pages/TryOnPricing";
import VerifySchool from "@/pages/VerifySchool";
import ProductDetail from "@/pages/ProductDetail";
import CategorySilo from "@/pages/CategorySilo";
import BrandSilo from "@/pages/BrandSilo";
import Cart from "@/pages/Cart";
import CheckoutReturn from "@/pages/CheckoutReturn";
import ThankYou from "@/pages/ThankYou";
import Auth from "@/pages/Auth";
import Account from "@/pages/Account";
import FamilyAccept from "@/pages/FamilyAccept";
import ChildLookbook from "@/pages/ChildLookbook";
import HomecomingEvent from "@/pages/HomecomingEvent";
import Schools from "@/pages/Schools";
import SchoolPortal from "@/pages/SchoolPortal";
import CountyHub from "@/pages/CountyHub";
import CityHub from "@/pages/CityHub";
import LockedLanding from "@/pages/LockedLanding";
import Lookbook from "@/pages/Lookbook";
import PromCup from "@/pages/PromCup";
import CompareLooks from "@/pages/CompareLooks";
import StyleMatch from "@/pages/StyleMatch";
import Ambassadors from "@/pages/Ambassadors";
import Memories from "@/pages/Memories";
import Squads from "@/pages/Squads";
import Boosters from "@/pages/Boosters";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Returns from "@/pages/legal/Returns";
import Shipping from "@/pages/legal/Shipping";
import Accessibility from "@/pages/legal/Accessibility";
import Contact from "@/pages/legal/Contact";
// import About from "@/pages/About"; // Hidden until Heather's real story is finalized
import Support from "@/pages/Support";
import NotFound from "@/pages/NotFound";

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <TryOnCreditsProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-center" toastOptions={{ style: { fontFamily: "Manrope", borderRadius: 2 } }} />
          <SideCart />
          <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/try-on" element={<TryOn />} />
            <Route path="/try-on/pricing" element={<TryOnPricing />} />
            <Route path="/auth/verify-school" element={<VerifySchool />} />
            <Route path="/family/accept" element={<FamilyAccept />} />
            <Route path="/family/lookbook/:studentId" element={<ChildLookbook />} />
            <Route path="/events/homecoming-2026" element={<HomecomingEvent />} />
            {/* Legacy /shop URL redirects to the quiz-first Try-On experience */}
            <Route path="/shop" element={<Navigate to="/try-on" replace />} />
            <Route path="/shop/*" element={<Navigate to="/try-on" replace />} />
            <Route path="/shop-archive" element={<Shop />} />
            {/* Legacy product URL — auto-redirects to canonical silo URL */}
            <Route path="/product/:id" element={<ProductDetail />} />
            {/* Bruce Clay silo — category hubs */}
            <Route path="/prom-dresses" element={<CategorySilo />} />
            <Route path="/homecoming-dresses" element={<CategorySilo />} />
            <Route path="/graduation-dresses" element={<CategorySilo />} />
            <Route path="/tuxedos" element={<CategorySilo />} />
            <Route path="/accessories" element={<CategorySilo />} />
            <Route path="/shoes" element={<CategorySilo />} />
            {/* Standalone brand hub — one URL per brand, cross-category */}
            <Route path="/brand/:brandSlug" element={<BrandSilo />} />
            {/* Legacy city hub — kept for backward compat under explicit /city/ prefix */}
            <Route path="/prom-dresses/city/:slug" element={<CityHub />} />
            {/* Silo PDP (with optional color) — 2 segments after category */}
            <Route path="/prom-dresses/:productSlug" element={<ProductDetail />} />
            <Route path="/prom-dresses/:productSlug/:colorSlug" element={<ProductDetail />} />
            <Route path="/homecoming-dresses/:productSlug" element={<ProductDetail />} />
            <Route path="/homecoming-dresses/:productSlug/:colorSlug" element={<ProductDetail />} />
            <Route path="/graduation-dresses/:productSlug" element={<ProductDetail />} />
            <Route path="/graduation-dresses/:productSlug/:colorSlug" element={<ProductDetail />} />
            <Route path="/tuxedos/:productSlug" element={<ProductDetail />} />
            <Route path="/tuxedos/:productSlug/:colorSlug" element={<ProductDetail />} />
            <Route path="/accessories/:productSlug" element={<ProductDetail />} />
            <Route path="/accessories/:productSlug/:colorSlug" element={<ProductDetail />} />
            <Route path="/shoes/:productSlug" element={<ProductDetail />} />
            <Route path="/shoes/:productSlug/:colorSlug" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout/return" element={<CheckoutReturn />} />
            <Route path="/thank-you/:orderId" element={<ThankYou />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="/register" element={<Navigate to="/auth?mode=signup" replace />} />
            <Route path="/signup" element={<Navigate to="/auth?mode=signup" replace />} />
            <Route path="/parents" element={<Navigate to="/auth?mode=parent" replace />} />
            <Route path="/account" element={<Account />} />
            <Route path="/schools" element={<Schools />} />
            <Route path="/schools/county/:slug" element={<CountyHub />} />
            <Route path="/locked/:productId" element={<LockedLanding />} />
            <Route path="/lookbook" element={<Lookbook />} />
            <Route path="/prom-cup" element={<PromCup />} />
            <Route path="/compare" element={<CompareLooks />} />
            <Route path="/style-match" element={<StyleMatch />} />
            <Route path="/ambassadors" element={<Ambassadors />} />
            <Route path="/schools/:id" element={<SchoolPortal />} />
            <Route path="/portal" element={<SchoolPortal />} />
            <Route path="/memories" element={<Memories />} />
            <Route path="/squads" element={<Squads />} />
            <Route path="/boosters" element={<Boosters />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/returns" element={<Returns />} />
            <Route path="/shipping" element={<Shipping />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/contact" element={<Contact />} />
            {/* /about hidden until Heather's real story is finalized */}
            {/* <Route path="/about" element={<About />} /> */}
            <Route path="/support" element={<Support />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
      </TryOnCreditsProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
